// src/automation/components/AutomationCover.js
//
// The Automation console landing / dashboard (default view). Combines the
// overview + analytics into one page:
//   • header with current week / month / year / timestamp
//   • KPI cards: Total runs, Time saved, Tasks tracked, Success rate
//   • Recent runs table (task, when, Scheduled vs manual+login, status)
//   • compact "Automations by section" list
//
// Data: config-derived counts (from sections) + run history (run_summary /
// list_runs). Run-based numbers populate once tasks run with the deployed
// backend; config counts are immediate.

import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Clock, Zap, ListChecks, Power, History, CalendarClock, Trash2 } from "lucide-react";
import { getRunSummary, listRuns, clearRunHistory } from "../api/automation_data";
import "wbr/components/coverpage/Coverpage.css";

function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function formatMinutes(mins) {
  if (!mins) return "0 min";
  if (mins < 60) return `${mins} min`;
  const hrs = mins / 60;
  if (hrs < 24) return `${hrs.toFixed(1)} hr`;
  return `${(hrs / 24).toFixed(1)} d`;
}

function Kpi({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className={`auto-kpi ${tone ? `auto-kpi--${tone}` : ""}`}>
      <span className="auto-kpi-value">
        {Icon && <Icon size={16} className="auto-kpi-icon" />} {value}
      </span>
      <span className="auto-kpi-label">{label}</span>
      {hint && <span className="auto-kpi-hint">{hint}</span>}
    </div>
  );
}

export default function AutomationCover({ sections = [], loading, onOpenTask, isAdmin = false }) {
  const qc = useQueryClient();
  const { data: summary = {} } = useQuery({
    queryKey: ["automationRunSummary"],
    queryFn: getRunSummary,
    staleTime: 2 * 60 * 1000,
  });

  const clearMutation = useMutation({
    mutationFn: () => clearRunHistory(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["automationRuns"] });
      qc.invalidateQueries({ queryKey: ["automationRunSummary"] });
    },
  });
  const onClearHistory = () => {
    if (window.confirm("Clear ALL run history? This wipes the runs table and per-task summary. This cannot be undone.")) {
      clearMutation.mutate();
    }
  };
  // Pull a deep slice of history so the windowed metrics (24h / 7d / this week /
  // this month) are exact rather than estimated from the last few runs. The
  // recent-runs table still only renders the newest handful.
  const { data: runs = [] } = useQuery({
    queryKey: ["automationRuns"],
    queryFn: () => listRuns({ limit: 2000 }),
    staleTime: 2 * 60 * 1000,
  });

  const now = new Date();
  const meta = `Week ${isoWeek(now)} · ${now.toLocaleString(undefined, {
    month: "long",
  })} ${now.getFullYear()} · ${now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;

  const totalTasks = sections.reduce((n, s) => n + (s.tasks?.length || 0), 0);

  // Config-derived: active = enabled & not hidden.
  const activeCount = sections.reduce(
    (n, s) => n + (s.tasks || []).filter((t) => t.enabled && !t.hidden).length,
    0
  );

  // Run-derived aggregates.
  const totalRuns = Object.values(summary).reduce((n, s) => n + (s?.count || 0), 0);
  const tasksTracked = Object.keys(summary).length;

  // Per-task minutes-saved (from config, carried on the section task objects).
  const minutesByTask = {};
  for (const s of sections) for (const t of s.tasks || []) minutesByTask[t.name] = t.minutes_saved_per_run || 0;

  // Window boundaries (local time): 24h ago, 7d ago, start-of-week (Monday),
  // start-of-month.
  const nowMs = now.getTime();
  const dayAgo = nowMs - 24 * 60 * 60 * 1000;
  const weekAgo = nowMs - 7 * 24 * 60 * 60 * 1000;
  const weekStart = (() => {
    const d = new Date(now);
    const dow = (d.getDay() + 6) % 7; // 0 = Monday
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - dow);
    return d.getTime();
  })();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  // Single pass over the full history: count runs per window + minutes saved per
  // window (minutes come from the triggering task's config). We only credit time
  // saved to successful runs — a failed run didn't save anyone any time.
  let runs24h = 0;
  let runs7d = 0;
  let savedWeek = 0;
  let savedMonth = 0;
  for (const r of runs) {
    const t = new Date(r.timestamp).getTime();
    if (Number.isNaN(t)) continue;
    if (t >= dayAgo) runs24h += 1;
    if (t >= weekAgo) runs7d += 1;
    if (r.success) {
      const mins = minutesByTask[r.task] || 0;
      if (t >= weekStart) savedWeek += mins;
      if (t >= monthStart) savedMonth += mins;
    }
  }

  // Recent-runs table: show only the last 24h so the page stays compact. The
  // full history still powers the KPIs above; the table is scrollable.
  const recentRuns = runs.filter((r) => {
    const t = new Date(r.timestamp).getTime();
    return !Number.isNaN(t) && t >= dayAgo;
  });

  // Last run across all tasks (max last_run from summary, fallback to newest run).
  const lastRunIso =
    Object.values(summary)
      .map((s) => s?.last_run)
      .filter(Boolean)
      .sort()
      .slice(-1)[0] || runs[0]?.timestamp;

  // All-time time saved = sum over tasks of count × minutes_saved_per_run.
  const timeSaved = Object.entries(summary).reduce(
    (n, [task, s]) => n + (s?.count || 0) * (minutesByTask[task] || 0),
    0
  );

  return (
    <div className="cp-container">
      <div className="cp-card-one">
        <div className="cp-card">
          <div className="cp-header-banner">
            <div className="cp-header-content">
              <div className="cp-header-left">
                <h1 className="cp-header-title">Automation Console</h1>
                <p className="cp-header-subtitle">{meta}</p>
              </div>
              <div className="cp-header-right">
                <h2 className="cp-header-org">
                  {loading ? "Loading…" : `${totalTasks} automation${totalTasks === 1 ? "" : "s"}`}
                </h2>
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <div className="auto-kpi-strip">
            <Kpi
              icon={Zap}
              label="Total runs"
              value={totalRuns.toLocaleString()}
              hint={`${runs7d.toLocaleString()} in last 7d`}
            />
            <Kpi
              icon={History}
              label="Runs (24h)"
              value={runs24h.toLocaleString()}
              hint={`${runs7d.toLocaleString()} in 7d`}
            />
            <Kpi
              icon={Clock}
              label="Saved this week"
              value={formatMinutes(savedWeek)}
              hint={`${formatMinutes(savedMonth)} this month`}
            />
            <Kpi
              icon={Clock}
              label="Saved all-time"
              value={formatMinutes(timeSaved)}
            />
            <Kpi
              icon={Power}
              label="Active"
              value={activeCount}
              hint={`${totalTasks} total`}
              tone="ok"
            />
            <Kpi icon={CalendarClock} label="Last run" value={timeAgo(lastRunIso)} />
            <Kpi icon={ListChecks} label="Tasks tracked" value={tasksTracked} />
          </div>
        </div>
      </div>

      {/* Recent runs */}
      <div className="cp-card-two">
        <div className="cp-card">
          <div className="auto-runs-head">
            <h4 className="cp-section-title">
              Recent runs <span className="auto-runs-sub">· last 24h ({recentRuns.length})</span>
            </h4>
            {isAdmin && runs.length > 0 && (
              <button
                className="auto-clear-btn"
                onClick={onClearHistory}
                disabled={clearMutation.isPending}
                title="Clear all run history (admin)"
              >
                <Trash2 size={13} /> {clearMutation.isPending ? "Clearing…" : "Clear history"}
              </button>
            )}
          </div>
          {recentRuns.length === 0 ? (
            <div className="auto-empty">
              {runs.length === 0
                ? "No runs recorded yet."
                : "No runs in the last 24 hours."}
            </div>
          ) : (
            <div className="auto-table-wrap auto-table-wrap--scroll">
              <table className="auto-table">
                <thead>
                  <tr><th>Task</th><th>When</th><th>Run by</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {recentRuns.map((r, i) => {
                    const scheduled = /scheduled/.test(r.type || "");
                    return (
                      <tr key={i}>
                        <td className="auto-table-mono">{r.task}</td>
                        <td>{fmtWhen(r.timestamp)}</td>
                        <td>
                          {scheduled ? (
                            <span className="auto-run-type auto-run-type--sched">Scheduled</span>
                          ) : (
                            <span className="auto-run-type auto-run-type--manual">
                              {r.actor ? r.actor : "manual"}
                              {/test/.test(r.type || "") ? " · test" : ""}
                            </span>
                          )}
                        </td>
                        <td>
                          {r.success ? (
                            <span className="auto-status auto-status--ok"><CheckCircle2 size={13} /> Success</span>
                          ) : (
                            <span className="auto-status auto-status--err"><XCircle size={13} /> Failed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Automations by section — compact */}
      {!loading && sections.length > 0 && (
        <div className="cp-card-two">
          <div className="cp-card">
            <h4 className="cp-section-title">Automations</h4>
            <div className="auto-cover-sections auto-cover-sections--compact">
              {sections.map((s) => (
                <div className="auto-cover-section" key={s.key}>
                  <div className="auto-cover-section-head">
                    <span className="auto-cover-section-title">{s.label}</span>
                    <span className="auto-cover-section-count">{s.tasks?.length || 0}</span>
                  </div>
                  <div className="auto-cover-chips">
                    {(s.tasks || []).map((t) => (
                      <button
                        key={t.name}
                        className="auto-cover-chip"
                        onClick={() => onOpenTask && onOpenTask(t.name)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
